<template lang="pug">
.purchase-reconciliation(v-loading="loading")

  section
    el-header
      el-col(:span="12")
        el-page-header(@back="back" content="Fechamento do mês: pedidos e notas de entrada")
      el-col.actions(:span="12")
        el-date-picker(v-model="month" type="month" value-format="yyyy-MM" format="MM/yyyy" :clearable="false" @change="load")
        el-button(icon="el-icon-refresh" :loading="recalculating" @click="recalculate") Recalcular custos
        el-button(type="primary" icon="el-icon-download" :loading="downloading" @click="download") Exportar XLSX

    p.summary(v-if="reconciliation")
      | {{ reconciliation.purchaseOrders.length }} pedidos atendidos no mês, {{ ordersWithIssues }} com pendências ·
      | {{ reconciliation.linkedInvoiceCount }} notas no mês, somando {{ formatCurrency(reconciliation.linkedInvoicesTotal) }} ·
      | {{ reconciliation.unlinkedInvoices.length }} notas sem pedido
    p.summary(v-if="totals")
      | Custo bruto {{ formatCurrency(totals.grossCost) }} ·
      | créditos de ICMS, PIS e COFINS {{ formatCurrency(credits) }} ·
      | compras líquidas {{ formatCurrency(totals.netCost) }}
      template(v-if="totals.itemsPendingMatch")
        |  · {{ totals.itemsPendingMatch }} itens ainda sem produto

    template(v-if="reconciliation")
      .section-title
        h4 Pedidos atendidos no mês
        el-switch(v-model="onlyIssues" active-text="Somente com pendências")

      el-table(:data="visibleOrders" size="mini" row-key="purchaseOrder.blingId")
        el-table-column(label="Atendido em" width="150")
          template(slot-scope="scope")
            | {{ formatDateTime(scope.row.purchaseOrder.fulfilledAt) }}
            el-button.inline(type="text" icon="el-icon-edit" @click="openFulfilledAtDialog(scope.row.purchaseOrder)")
        el-table-column(label="Pedido" width="80")
          template(slot-scope="scope") {{ scope.row.purchaseOrder.number }}
        el-table-column(label="Fornecedor" min-width="180" show-overflow-tooltip)
          template(slot-scope="scope") {{ scope.row.purchaseOrder.supplierName || '-' }}
        el-table-column(label="Observação interna" min-width="180" show-overflow-tooltip)
          template(slot-scope="scope") {{ scope.row.purchaseOrder.internalNotes }}
        el-table-column(label="Total do pedido" width="130" align="right")
          template(slot-scope="scope") {{ formatCurrency(scope.row.purchaseOrder.total) }}
        el-table-column(label="Notas" min-width="170")
          template(slot-scope="scope")
            el-tag.invoice(v-for="invoice in scope.row.invoices" :key="invoice.id" size="mini" closable
              :type="invoice.status === 'CANCELLED' || invoice.disregard ? 'danger' : 'info'"
              @click="openInvoice(invoice)" @close="unlink(scope.row.purchaseOrder, invoice)")
              | NF {{ invoice.number }}
        el-table-column(label="Diferença" width="110" align="right")
          template(slot-scope="scope") {{ formatCurrency(scope.row.totalDifference) }}
        el-table-column(label="Pendências" min-width="200")
          template(slot-scope="scope")
            el-tag.issue(v-for="issue in scope.row.issues" :key="issue" size="mini" type="warning") {{ reconciliationIssueLabels[issue] }}
        el-table-column(width="130" align="center" fixed="right")
          template(slot-scope="scope")
            el-tooltip(content="Vincular nota" placement="top")
              el-button(size="mini" type="primary" plain icon="el-icon-link" @click="openLinkInvoiceDialog(scope.row.purchaseOrder)")
            el-tooltip(content="Atualizar do Bling" placement="top")
              el-button(size="mini" plain icon="el-icon-refresh" :loading="resyncing === scope.row.purchaseOrder.blingId" @click="resync(scope.row.purchaseOrder)")

      h4 Notas sem pedido vinculado
      p.hint Notas de compra e bonificação emitidas desde dois meses antes do mês escolhido que nenhum pedido referencia.

      el-table(:data="reconciliation.unlinkedInvoices" size="mini")
        el-table-column(label="Emissão" width="110")
          template(slot-scope="scope") {{ formatDate(scope.row.issuedAt) }}
        el-table-column(label="Número" width="100")
          template(slot-scope="scope")
            el-button(type="text" @click="openInvoice(scope.row)") {{ scope.row.number }}
        el-table-column(prop="issuerName" label="Fornecedor" min-width="200" show-overflow-tooltip)
        el-table-column(label="Valor total" width="130" align="right")
          template(slot-scope="scope") {{ formatCurrency(scope.row.totals.totalAmount) }}
        el-table-column(width="90" align="center" fixed="right")
          template(slot-scope="scope")
            el-tooltip(content="Vincular a um pedido do mês" placement="top")
              el-button(size="mini" type="primary" plain icon="el-icon-link" @click="openLinkOrderDialog(scope.row)")

  el-dialog(title="Vincular nota ao pedido" :visible.sync="linkInvoiceDialog.visible" width="560px")
    p(v-if="linkInvoiceDialog.order") Pedido {{ linkInvoiceDialog.order.number }} · {{ linkInvoiceDialog.order.supplierName }}
    el-select.stretched(v-model="linkInvoiceDialog.invoiceId" filterable remote :remote-method="searchInvoices"
      :loading="linkInvoiceDialog.searching" placeholder="Busque pelo número, fornecedor, CNPJ ou chave")
      el-option(v-for="invoice in linkInvoiceDialog.options" :key="invoice.id" :value="invoice.id"
        :label="`NF ${invoice.number} · ${invoice.issuerName} · ${formatCurrency(invoice.totals.totalAmount)}`")
    span(slot="footer")
      el-button(@click="linkInvoiceDialog.visible = false") Cancelar
      el-button(type="primary" :disabled="!linkInvoiceDialog.invoiceId" :loading="saving" @click="confirmLinkInvoice") Vincular

  el-dialog(title="Vincular nota a um pedido" :visible.sync="linkOrderDialog.visible" width="560px")
    p(v-if="linkOrderDialog.invoice") NF {{ linkOrderDialog.invoice.number }} · {{ linkOrderDialog.invoice.issuerName }}
    el-select.stretched(v-model="linkOrderDialog.blingId" filterable placeholder="Pedido atendido no mês")
      el-option(v-for="row in reconciliation ? reconciliation.purchaseOrders : []" :key="row.purchaseOrder.blingId"
        :value="row.purchaseOrder.blingId"
        :label="`Pedido ${row.purchaseOrder.number} · ${row.purchaseOrder.supplierName || '-'} · ${row.purchaseOrder.internalNotes || ''}`")
    span(slot="footer")
      el-button(@click="linkOrderDialog.visible = false") Cancelar
      el-button(type="primary" :disabled="!linkOrderDialog.blingId" :loading="saving" @click="confirmLinkOrder") Vincular

  el-dialog(title="Corrigir data de atendimento" :visible.sync="fulfilledAtDialog.visible" width="460px")
    p
      | A data em que o pedido foi atendido define o mês em que suas notas entram no CMV.
    el-date-picker.stretched(v-model="fulfilledAtDialog.fulfilledAt" type="datetime" format="dd/MM/yyyy HH:mm" :clearable="false")
    span(slot="footer")
      el-button(@click="fulfilledAtDialog.visible = false") Cancelar
      el-button(type="primary" :loading="saving" @click="confirmFulfilledAt") Salvar

</template>
<script setup>
import moment from 'moment-timezone'
import { computed, getCurrentInstance, onMounted, ref } from 'vue'
import { useRouter } from 'vue2-helpers/vue-router'

import PurchaseCostReportService from '@/services/fiscal/PurchaseCostReportService'
import PurchaseInvoiceService from '@/services/fiscal/PurchaseInvoiceService'
import PurchaseReconciliationService from '@/services/fiscal/PurchaseReconciliationService'
import { useMessage } from '@/utils/Message'
import {
  formatCurrency, formatDate, formatDateTime, reconciliationIssueLabels,
} from '../purchase-invoice/labels'

const router = useRouter()
const message = useMessage()
const confirm = getCurrentInstance().proxy.$confirm

const month = ref(moment().tz('America/Sao_Paulo').subtract(1, 'month').format('YYYY-MM'))
const reconciliation = ref(null)
const totals = ref(null)
const loading = ref(false)
const downloading = ref(false)
const recalculating = ref(false)
const saving = ref(false)
const resyncing = ref(null)
const onlyIssues = ref(false)

const linkInvoiceDialog = ref({ visible: false, order: null, invoiceId: null, options: [], searching: false })
const linkOrderDialog = ref({ visible: false, invoice: null, blingId: null })
const fulfilledAtDialog = ref({ visible: false, order: null, fulfilledAt: null })

const credits = computed(() => (totals.value
  ? Number(totals.value.icmsCredit) + Number(totals.value.pisCredit) + Number(totals.value.cofinsCredit)
  : 0))

const ordersWithIssues = computed(() => (reconciliation.value
  ? reconciliation.value.purchaseOrders.filter(row => row.issues.length).length
  : 0))

const visibleOrders = computed(() => {
  if (!reconciliation.value) return []

  const rows = onlyIssues.value
    ? reconciliation.value.purchaseOrders.filter(row => row.issues.length)
    : reconciliation.value.purchaseOrders

  return [...rows].sort((a, b) => Math.min(b.issues.length, 1) - Math.min(a.issues.length, 1))
})

const describeError = error => error.description || error.message

const back = () => router.back()

const openInvoice = invoice => router.push({ name: 'purchase-invoice-detail', params: { id: invoice.id } })

const load = async () => {
  loading.value = true

  try {
    const [loadedReconciliation, loadedTotals] = await Promise.all([
      PurchaseReconciliationService.reconcile(month.value),
      PurchaseCostReportService.totals(month.value),
    ])
    reconciliation.value = loadedReconciliation
    totals.value = loadedTotals
  } catch (error) {
    message.error(`Não foi possível carregar o mês: ${describeError(error)}`)
  } finally {
    loading.value = false
  }
}

const download = async () => {
  downloading.value = true

  try {
    await PurchaseCostReportService.download(month.value)
  } catch (error) {
    message.error(describeError(error))
  } finally {
    downloading.value = false
  }
}

const recalculate = async () => {
  try {
    await confirm(
      'Recalcular o custo dos itens das notas do mês com as regras e a lista de NCMs com crédito atuais? '
      + 'Se o mês já foi enviado à contabilidade, os valores podem mudar.',
    )
  } catch (cancelled) {
    return
  }

  recalculating.value = true

  try {
    const result = await PurchaseCostReportService.recalculate(month.value)
    message.success(`${result.recalculated} itens recalculados.`)
    await load()
  } catch (error) {
    message.error(describeError(error))
  } finally {
    recalculating.value = false
  }
}

const runAndReload = async (action, successMessage) => {
  saving.value = true

  try {
    await action()
    message.success(successMessage)
    await load()
    return true
  } catch (error) {
    message.error(describeError(error))
    return false
  } finally {
    saving.value = false
  }
}

const unlink = async (order, invoice) => {
  try {
    await confirm(`Desvincular a NF ${invoice.number} do pedido ${order.number}? O pedido passa a ser vinculado só manualmente.`)
  } catch (cancelled) {
    return
  }

  await runAndReload(() => PurchaseReconciliationService.unlink(order.blingId, invoice.id), 'Nota desvinculada.')
}

const resync = async (order) => {
  resyncing.value = order.blingId

  try {
    await PurchaseReconciliationService.resync(order.blingId)
    message.success(`Pedido ${order.number} atualizado do Bling.`)
    await load()
  } catch (error) {
    message.error(`Não foi possível atualizar do Bling: ${describeError(error)}`)
  } finally {
    resyncing.value = null
  }
}

const openLinkInvoiceDialog = (order) => {
  const options = reconciliation.value.unlinkedInvoices
  linkInvoiceDialog.value = { visible: true, order, invoiceId: null, options, searching: false }
}

const searchInvoices = async (query) => {
  if (!query) return

  linkInvoiceDialog.value.searching = true

  try {
    const result = await PurchaseInvoiceService.search({ query, pageSize: 20 })
    linkInvoiceDialog.value.options = result.results
  } catch (error) {
    message.error(describeError(error))
  } finally {
    linkInvoiceDialog.value.searching = false
  }
}

const confirmLinkInvoice = async () => {
  const { order, invoiceId } = linkInvoiceDialog.value
  const linked = await runAndReload(() => PurchaseReconciliationService.link(order.blingId, invoiceId), 'Nota vinculada.')
  if (linked) linkInvoiceDialog.value.visible = false
}

const openLinkOrderDialog = (invoice) => {
  linkOrderDialog.value = { visible: true, invoice, blingId: null }
}

const confirmLinkOrder = async () => {
  const { invoice, blingId } = linkOrderDialog.value
  const linked = await runAndReload(() => PurchaseReconciliationService.link(blingId, invoice.id), 'Nota vinculada.')
  if (linked) linkOrderDialog.value.visible = false
}

const openFulfilledAtDialog = (order) => {
  fulfilledAtDialog.value = { visible: true, order, fulfilledAt: new Date(order.fulfilledAt) }
}

const confirmFulfilledAt = async () => {
  const { order, fulfilledAt } = fulfilledAtDialog.value
  const saved = await runAndReload(
    () => PurchaseReconciliationService.updateFulfilledAt(order.blingId, new Date(fulfilledAt).toISOString()),
    'Data de atendimento corrigida.',
  )
  if (saved) fulfilledAtDialog.value.visible = false
}

onMounted(load)
</script>
<style lang="scss" scoped>
.summary {
  margin: 0 0 8px;
}

.actions {
  text-align: right;

  .el-button {
    margin-left: 8px;
  }
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

h4 {
  margin: 20px 0 8px;
}

.hint {
  margin: 0 0 8px;
  color: #5e7382;
}

.el-tag.invoice {
  margin: 2px 4px 2px 0;
  cursor: pointer;
}

.el-tag.issue {
  margin: 2px 4px 2px 0;
}

.el-button.inline {
  margin-left: 4px;
  padding: 0;
}
</style>
